import { useSelector } from 'react-redux';

import { selectUserProjectId } from 'core/userContext';
import { SMSContentDefinition } from 'modules/graphql/types';
import { useSMSUserData } from 'modules/templateEditor/hooks/useSMSUserData';
import {
  AndroidSend,
  ArrowBack,
  ChevronRight16,
  ChevronRight24,
  IosAvatar,
  Overflow,
} from 'ui/icons';
import { colors } from 'ui/theme';

import { displayContent } from '../../common/content';
import { OS, TemplateEditViews } from '../../common/types';
import {
  getDateFull,
  getRelativeDayInfo,
  getTime,
  getTimeSimple,
  shortenLinkInMessage,
} from '../../common/utils';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { usePreviewWithDataContext } from '../../context/PreviewWithDataContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { useSMSLinkShortening } from '../context/SMSLinkShorteningContext';
import { useSMSPreviewContext } from '../context/SMSPreviewContext';
import {
  avatarIos24,
  linkText,
  messageBody,
  messageBodyAndroidDate,
  messageBodyAndroidMessage,
  messageBodyAndroidMessageContainer,
  messageBodyAndroidMessageTime,
  messageBodyIosDateTime,
  messageBodyIosMessage,
  messageBodyMedia,
  messageContactCardAndroidDisplayNameText,
  messageContactCardAndroidMessage,
  messageContactCardAvatarImage,
  messageContactCardIosDisplayNameText,
  messageContactCardIosMessage,
  messageFooterAndroid,
  messageFooterAndroidInput,
  messageFooterAndroidSend,
  messageFooterIos,
  messageHeaderAndroid,
  messageHeaderAndroidCenter,
  messageHeaderAndroidSide,
  messageHeaderIos,
  messageHeaderIosCenter,
  messageHeaderIosSide,
  messageInputIos,
  rotate180,
} from './styles';

type Props = {
  selectedOs: OS;
};

export const Message = ({ selectedOs }: Props) => {
  const { liveTemplate } = useEditableFieldsContext();
  const { locale, view } = useQueryParamContext();
  const { messageContent, previewContactCard, fullSmsMessage } = useSMSPreviewContext();
  const projectId = useSelector(selectUserProjectId);
  const { isSMSLinkShorteningEnabled, shortlinkLength, defaultSmsLinkShorteningDomain } =
    useSMSLinkShortening();
  const { isDataFeedDataEmpty, isUserDataEmpty, userDataIsValid, userData } =
    usePreviewWithDataContext();
  const { userIsUSOrCanada } = useSMSUserData(userDataIsValid, userData);

  const usePopulatedField =
    view === TemplateEditViews.preview && (!isDataFeedDataEmpty || !isUserDataEmpty);
  const smsData = (liveTemplate?.mediumData || {}) as SMSContentDefinition;
  const { trackingDomain } = smsData;
  // note: this date and time appear in a localized preview, so we specify the locale of the template
  const dateTime = new Date();
  const timeSimple = getTimeSimple(dateTime, locale);
  const time = getTime(dateTime, locale);
  const date = getDateFull(dateTime, locale);
  const day = getRelativeDayInfo(dateTime.toString(), locale);
  const message = usePopulatedField ? messageContent : fullSmsMessage;

  const messageMaybeWithShortenLink =
    shortenLinkInMessage(
      message,
      trackingDomain ?? defaultSmsLinkShorteningDomain ?? '',
      projectId,
      shortlinkLength,
      isSMSLinkShorteningEnabled,
    ) || '';

  const renderMedia = (platform: OS) => {
    if (smsData.imageUrl || previewContactCard) {
      if (userIsUSOrCanada) {
        return (
          // contact card render within US/Canada handled in main render body so skip render here
          smsData.imageUrl && (
            <div
              data-test={platform === OS.android && 'android-imageUrl'}
              style={{ ...messageBodyMedia, backgroundImage: `url(${smsData.imageUrl})` }}
            />
          )
        );
      } else {
        return (
          <>
            <br />
            <a
              href={smsData.imageUrl || undefined}
              style={{
                ...(platform === OS.ios ? messageBodyIosMessage : messageBodyAndroidMessage),
                ...linkText,
              }}
              target="_blank"
            >
              {`https://${trackingDomain || 'itbl.co'}/p9sB7MiV`}
            </a>
          </>
        );
      }
    }
    return null;
  };

  return selectedOs === OS.ios ? (
    <>
      <div style={messageHeaderIos}>
        <div style={messageHeaderIosSide}>
          <ChevronRight24 style={rotate180} color={`#3494FB`} height={17} width={17} />
        </div>
        <div style={messageHeaderIosCenter}>
          <div style={avatarIos24}>
            <IosAvatar color={colors.white} height={34} width={34} />
          </div>
        </div>
        <div style={messageHeaderIosSide}></div>
      </div>
      <div style={messageBody}>
        <div style={messageBodyIosDateTime}>{displayContent.strTextMessage}</div>
        <div style={messageBodyIosDateTime}>
          {day}, {timeSimple}
        </div>
        {renderMedia(OS.ios)}
        <div data-test="ios-message" style={messageBodyIosMessage}>
          {messageMaybeWithShortenLink.trim()}
        </div>
        {previewContactCard && userIsUSOrCanada && (
          <div style={messageContactCardIosMessage}>
            <div data-test="ios-displayName" style={messageContactCardIosDisplayNameText}>
              {previewContactCard?.displayName}
            </div>
            <img
              data-test="ios-imageUrl"
              style={messageContactCardAvatarImage}
              src={previewContactCard?.imageUrl}
            />
            <ChevronRight16 color="#9597A3" />
          </div>
        )}
      </div>
      <div style={messageFooterIos}>
        <div style={messageInputIos}>{displayContent.strTextMessage}</div>
      </div>
    </>
  ) : (
    <>
      <div style={messageHeaderAndroid}>
        <div style={messageHeaderAndroidSide}>
          <ArrowBack color={`#35B8C0`} height={22} width={22} />
        </div>
        <div style={messageHeaderAndroidCenter}></div>
        <div style={messageHeaderAndroidSide}>
          <Overflow color={`#35B8C0`} height={22} width={22} />
        </div>
      </div>
      <div style={messageBody}>
        <div style={messageBodyAndroidDate}>{date}</div>
        {renderMedia(OS.android)}
        <div style={messageBodyAndroidMessageContainer}>
          <div data-test="android-message" style={messageBodyAndroidMessage}>
            {messageMaybeWithShortenLink.trim()}
          </div>
          <div style={messageBodyAndroidMessageTime}>{time}</div>
        </div>
        {previewContactCard && userIsUSOrCanada && (
          <div style={messageBodyAndroidMessageContainer}>
            <div style={messageBodyAndroidMessage}>
              <div style={messageContactCardAndroidMessage}>
                <div
                  data-test="android-displayName"
                  style={messageContactCardAndroidDisplayNameText}
                >
                  {previewContactCard.displayName}
                </div>
                <img style={messageContactCardAvatarImage} src={previewContactCard?.imageUrl} />
              </div>
            </div>
          </div>
        )}
      </div>
      <div style={messageFooterAndroid}>
        <div style={messageFooterAndroidInput}>{displayContent.strTypeMessage}</div>
        <div style={messageFooterAndroidSend}>
          <AndroidSend color={colors.white} height={39} width={39} />
        </div>
      </div>
    </>
  );
};

export default Message;
